/**
 * Journal — the recap tab (spec §6). Chronological record of runs.
 *
 * Empty-state text must be a single block (never a Fragment under CSS grid —
 * Fragment children become separate grid cells and scatter across the pane).
 *
 * Replay: Play streams the build forward on Canvas. Click an entry to jump to
 * that point (later nodes cascade-hide). "Up to here" rolls back the view;
 * "From here" plays forward from that frame.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2, Circle, RefreshCw, Play, SkipBack, SkipForward, Crosshair, Pause,
} from 'lucide-react';
import type cytoscape from 'cytoscape';
import { useIntakeStore, type ReplayEntry } from '../../../stores/useIntakeStore';
import { useFusionStore } from '../../../stores/useFusionStore';
import {
  fetchCaseEvents,
  flushOutbox,
  mergeJournalEntries,
  readEventsCache,
  runEventToJournal,
  seedJournalEntries,
  writeEventsCache,
  type JournalEntry,
} from '../../../case-canvas/intake/journalSpine';

const fmt = (ts: number) => new Date(ts).toLocaleTimeString();
const fmtDay = (ts: number) => new Date(ts).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

function toChrono(entries: JournalEntry[]): ReplayEntry[] {
  return [...entries]
    .sort((a, b) => a.ts - b.ts)
    .map((e) => ({
      id: e.id,
      ts: e.ts,
      title: e.title,
      summary: e.summary,
      tier: e.tier,
      targetNodeId: e.targetNodeId,
      nodeLabel: e.nodeLabel,
      synthetic: e.synthetic,
      pending: e.pending,
    }));
}

export function Journal() {
  const runLog = useIntakeStore((s) => s.runLog);
  const caseId = useIntakeStore((s) => s.caseId);
  const replay = useIntakeStore((s) => s.replay);
  const startReplay = useIntakeStore((s) => s.startReplay);
  const setReplayStep = useIntakeStore((s) => s.setReplayStep);
  const setReplayPlaying = useIntakeStore((s) => s.setReplayPlaying);
  const stopReplay = useIntakeStore((s) => s.stopReplay);
  const select = useFusionStore((s) => s.select);
  const showDemo = useFusionStore((s) => s.showDemo);
  const [server, setServer] = useState<JournalEntry[] | null>(() =>
    caseId ? readEventsCache(caseId) : null,
  );
  const [loading, setLoading] = useState(false);
  /** Display order: build order (oldest→newest) matches replay; newest-first for live feed. */
  const [order, setOrder] = useState<'build' | 'newest'>('build');

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      await flushOutbox(caseId);
      const entries = await fetchCaseEvents(caseId);
      setServer(entries);
      writeEventsCache(caseId, entries);
    } catch {
      setServer((prev) => prev ?? []);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (!caseId) {
      setServer(null);
      return;
    }
    const cached = readEventsCache(caseId);
    if (cached) setServer(cached);
    void load();
  }, [caseId, runLog.length, load]);

  const focus = (nodeId?: string) => {
    if (!nodeId) return;
    const cy = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
    if (!cy || cy.destroyed()) return;
    const n = cy.getElementById(nodeId);
    if (n && n.length) {
      cy.animate({ center: { eles: n } }, { duration: 220 });
      select({ id: nodeId, kind: 'node' });
    }
  };

  const entries: JournalEntry[] = useMemo(() => {
    const optimistic = runLog.map(runEventToJournal);
    if (caseId) return mergeJournalEntries(server || [], optimistic);
    if (optimistic.length) return optimistic;
    if (showDemo) return seedJournalEntries();
    return [];
  }, [caseId, server, runLog, showDemo]);

  const chrono = useMemo(() => toChrono(entries), [entries]);
  const displayed = order === 'build' ? chrono : [...chrono].reverse();

  const activeId = replay
    ? replay.events[Math.min(replay.step, replay.events.length) - 1]?.id
    : undefined;

  const gotoCanvas = () => window.dispatchEvent(new CustomEvent('intake:goto-canvas'));

  /** Full play from the start — streams every run onto the canvas in order. */
  const replayAll = () => {
    if (!chrono.length) return;
    startReplay(chrono, { step: 1, playing: true });
    gotoCanvas();
  };

  /**
   * Cascade / rollback: show the graph as it looked after this run
   * (everything after is hidden). Paused so the officer can inspect.
   */
  const showUpTo = (entryId: string) => {
    const idx = chrono.findIndex((e) => e.id === entryId);
    if (idx < 0) return;
    startReplay(chrono, { step: idx + 1, playing: false });
    focus(chrono[idx].targetNodeId);
    gotoCanvas();
  };

  /** Play forward from this run (inclusive). */
  const playFrom = (entryId: string) => {
    const idx = chrono.findIndex((e) => e.id === entryId);
    if (idx < 0) return;
    startReplay(chrono, { step: idx + 1, playing: true });
    gotoCanvas();
  };

  /** Jump the active replay cursor to this entry without restarting. */
  const jumpTo = (entryId: string) => {
    const idx = chrono.findIndex((e) => e.id === entryId);
    if (idx < 0) return;
    if (replay && replay.events.length === chrono.length) {
      setReplayStep(idx + 1);
      setReplayPlaying(false);
      focus(chrono[idx].targetNodeId);
      gotoCanvas();
      return;
    }
    showUpTo(entryId);
  };

  const emptyCopy = caseId
    ? 'No runs yet. Go to the Build tab, add a card, and press Run — each run lands here in order.'
    : 'No runs in this session. Turn demo seed ON for a sample timeline, or sign in on /fusion so case_events persist.';

  return (
    <div className="h-full flex flex-col min-h-0" data-tour="fusion-journal">
      <div className="px-4 py-3 border-b border-border bg-card flex items-start justify-between gap-3 shrink-0">
        <div className="min-w-0">
          <div className="font-semibold text-sm">Journal</div>
          <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
            Append-only build history. Replay streams runs onto the canvas; jump to a point to
            cascade-hide later work, or play forward from any frame.
          </div>
        </div>
        <div className="shrink-0 flex flex-wrap items-center justify-end gap-1.5">
          <div className="flex rounded-md border border-border overflow-hidden text-[10px]">
            <button
              type="button"
              onClick={() => setOrder('build')}
              className={`px-2 py-1 ${order === 'build' ? 'bg-secondary text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/50'}`}
              title="Oldest first — same order as Replay"
            >
              Build order
            </button>
            <button
              type="button"
              onClick={() => setOrder('newest')}
              className={`px-2 py-1 border-l border-border ${order === 'newest' ? 'bg-secondary text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/50'}`}
              title="Newest first — live feed"
            >
              Newest
            </button>
          </div>
          {replay ? (
            <>
              <button
                type="button"
                onClick={() => setReplayPlaying(!replay.playing)}
                className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border border-border hover:bg-secondary"
                title={replay.playing ? 'Pause replay' : 'Resume replay'}
              >
                {replay.playing ? <Pause size={11} /> : <Play size={11} />}
                {replay.playing ? 'Pause' : 'Resume'}
              </button>
              <button
                type="button"
                onClick={stopReplay}
                className="text-[10px] px-2 py-1 rounded border border-border text-muted-foreground hover:text-foreground"
              >
                Exit replay
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={replayAll}
              disabled={chrono.length === 0}
              title="Replay this case on the canvas from the first run"
              className="flex items-center gap-1 text-[10px] font-medium text-primary-foreground bg-primary hover:bg-brand-gold-hover disabled:opacity-40 px-2.5 py-1 rounded"
            >
              <Play size={11} /> Replay all
            </button>
          )}
          {caseId && (
            <button
              type="button"
              onClick={() => void load()}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-2 py-1 rounded border border-border"
            >
              <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          )}
        </div>
      </div>

      {replay && (
        <div className="px-4 py-2 border-b border-primary/20 bg-primary/5 text-[11px] text-foreground flex items-center gap-2 shrink-0">
          <span className="font-semibold text-primary tabular-nums">
            Frame {replay.step} / {replay.events.length}
          </span>
          <span className="text-muted-foreground truncate">
            Showing the case after this run — later nodes are rolled back (hidden) until you step forward.
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0 p-4">
        {displayed.length === 0 ? (
          <div className="h-full min-h-[12rem] flex items-center justify-center">
            <p className="max-w-sm text-center text-sm text-muted-foreground leading-relaxed px-4">
              {emptyCopy}
            </p>
          </div>
        ) : (
          <ol className="relative border-l border-border ml-3 space-y-0">
            {displayed.map((e) => {
              const buildIdx = chrono.findIndex((c) => c.id === e.id);
              const stepN = buildIdx + 1;
              const isActive = e.id === activeId;
              const isFuture = replay ? stepN > replay.step : false;
              return (
                <li
                  key={e.id}
                  data-testid={`journal-entry-${e.id}`}
                  className={`relative ml-4 pb-4 last:pb-0 transition-opacity ${isFuture ? 'opacity-45' : ''}`}
                >
                  <span className="absolute -left-[1.35rem] mt-1.5 bg-card">
                    {e.tier === 'deterministic'
                      ? <CheckCircle2 size={14} className={isActive ? 'text-primary' : 'text-emerald-500'} />
                      : <Circle size={14} className={isActive ? 'text-primary' : 'text-muted-foreground'} />}
                  </span>

                  <button
                    type="button"
                    onClick={() => jumpTo(e.id)}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                      isActive
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-transparent hover:border-border hover:bg-secondary/40'
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground tabular-nums">
                        #{stepN}
                      </span>
                      <span className="text-[10px] text-muted-foreground tabular-nums" title={fmtDay(e.ts)}>
                        {fmt(e.ts)}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-foreground mt-0.5" data-testid="journal-entry-title">{e.title}</div>
                    {e.summary && e.summary !== e.title && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug" data-testid="journal-entry-summary">{e.summary}</div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {e.tier && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                          e.tier === 'deterministic'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : e.tier === 'review'
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                        }`}>
                          {e.tier}
                        </span>
                      )}
                      {e.synthetic && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">demo seed</span>
                      )}
                      {e.pending && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300">syncing</span>
                      )}
                      {isActive && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium">current frame</span>
                      )}
                    </div>
                  </button>

                  <div className="flex flex-wrap items-center gap-1 mt-1.5 pl-0.5">
                    <button
                      type="button"
                      onClick={() => showUpTo(e.id)}
                      title="Rollback view: hide everything after this run"
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded border border-transparent hover:border-border"
                    >
                      <SkipBack size={10} /> Up to here
                    </button>
                    <button
                      type="button"
                      onClick={() => playFrom(e.id)}
                      title="Play forward from this run"
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded border border-transparent hover:border-border"
                    >
                      <SkipForward size={10} /> From here
                    </button>
                    {e.targetNodeId && (
                      <button
                        type="button"
                        onClick={() => { focus(e.targetNodeId); gotoCanvas(); }}
                        className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline px-1.5 py-0.5"
                      >
                        <Crosshair size={10} /> On canvas
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
